/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { NewsRequestUtilService } from '../../utils/news-utils.service';
import { CommonDataParamsService } from '../../utils/common-data-params.service';
import { DataApiService } from './data-api-service';
import { catchError, map, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { Observable } from 'rxjs';
import { QueryApiService } from 'src/app/shared/services/customer-api/query-api.service';
import { RoutingService } from 'src/app/service/routing.service';
import { BookmarkApiService } from 'src/app/shared/services/customer-api/bookmark-api.service';
import { AuditApiService } from 'src/app/shared/services/customer-api/audit-api.service';
import { EntityInfoService } from '../metadata-api/entity-info.service';
import { ParamsBuilder } from '../../utils/params-builder';
import { TagUtils } from '../../utils/tag.utils';
import * as moment from 'moment';
import { mergeMap } from 'rxjs/operators';

/**
 * Lookback used when drilling into a score event. Must cover the score's
 * rolling window (`rolling_sum_period`, ~97 days = rating_stability 90 +
 * benchmark_duration 7) — a driver's contribution accumulates across that
 * whole span, so its articles routinely predate the selected timeframe.
 */
export const SCORE_EVENT_LOOKBACK_DAYS = 100;

@Injectable({
  providedIn: 'root',
})
export class NewsService {
  constructor(
    private dataApiService: DataApiService,
    private newsRequestUtil: NewsRequestUtilService,
    private commonDataParams: CommonDataParamsService,
    private entityInfoService: EntityInfoService,
    private queryApi: QueryApiService,
    private routing: RoutingService,
    private bookmarkApi: BookmarkApiService,
    private auditApi: AuditApiService,
  ) {}

  private processNewsResponse(respArg: any): Observable<any> {
    const resp = JSON.parse(JSON.stringify(respArg));
    const uniqueKeys = new Set<string>();

    respArg['news'].forEach((news: { [x: string]: any[] }) => {
      news['events'].forEach(
        (event: { [x: string]: { compound_key: string }[] }) => {
          event['participants'].forEach(
            (participant: { compound_key: string }) => {
              uniqueKeys.add(participant.compound_key);
            },
          );
        },
      );
    });

    if (Object.prototype.hasOwnProperty.call(resp, 'news')) {
      return this.entityInfoService.getEntitiesInfo([...uniqueKeys]).pipe(
        map((result) => this.enrichNewsResponse(resp, result)),
        catchError(() => of(this.enrichNewsResponse(resp, {}))),
      );
    } else {
      return of(resp);
    }
  }

  private enrichNewsResponse(resp: any, entityInfo: any): any {
    resp['news'].forEach((news: any, indexNews: number) => {
      news['events'].forEach((event: any, indexEvent: number) => {
        if (event.participants) {
          event.participants.forEach(
            (participant: any, indexParticipant: number) => {
              resp['news'][indexNews]['events'][indexEvent]['participants'][
                indexParticipant
              ] = entityInfo[participant.compound_key] || participant;
            },
          );
        }
        this.adjustSpanLocations(
          resp['news'][indexNews]['events'][indexEvent],
          news.locations,
        );
      });
      news['matches'].forEach((match: any) => {
        match['spans'].forEach((span: any) => {
          this.adjustSpanLocations(span, news.locations);
        });
      });
    });
    return resp;
  }

  private adjustSpanLocations(span: any, locations: any): void {
    if (span && locations) {
      if (span.end <= locations.title.end) {
        span.location = 'title';
        span.start -= locations.title.start;
        span.end -= locations.title.start;
      } else {
        span.location = 'content';
        span.start -= locations.content.start;
        span.end -= locations.content.start;
      }
    }
  }

  public getBookMarkedNews(params: any): Observable<any> {
    return this.bookmarkApi.getStars().pipe(
      map((response: any) => {
        const res = JSON.parse(JSON.stringify(response));
        const newsIds = res;
        return this.newsRequestUtil.createNewsRequest(
          { ...params.customParams, news_ids: { include: newsIds } },
          params.body,
        );
      }),
      switchMap((requestBody) =>
        this.dataApiService
          .post('news', requestBody, {}, 'v4')
          .pipe(switchMap((respArg) => this.processNewsResponse(respArg))),
      ),
    );
  }

  public getDismissedNews(params: any): Observable<any> {
    return this.auditApi.getDismissedIds().pipe(
      map((response: any) => {
        const res = JSON.parse(JSON.stringify(response));
        let resIds: any[] = [];
        for (const prop of Object.keys(res)) {
          resIds = resIds.concat(res[prop]);
        }
        return this.newsRequestUtil.createNewsRequest(
          { ...params.customParams, news_ids: { include: resIds } },
          params.body,
        );
      }),
      switchMap((requestBody) =>
        this.dataApiService
          .post('news', requestBody, {}, 'v4')
          .pipe(switchMap((respArg) => this.processNewsResponse(respArg))),
      ),
    );
  }

  public getFlaggedNews(params: any): Observable<any> {
    return this.auditApi.getAuditedIds().pipe(
      map((response: any) => {
        const res = JSON.parse(JSON.stringify(response));
        let resIds: any[] = [];
        for (const prop of Object.keys(res)) {
          resIds = resIds.concat(res[prop]);
        }
        return this.newsRequestUtil.createNewsRequest(
          { ...params.customParams, news_ids: { include: resIds } },
          params.body,
        );
      }),
      switchMap((requestBody) =>
        this.dataApiService
          .post('news', requestBody, {}, 'v4')
          .pipe(switchMap((respArg) => this.processNewsResponse(respArg))),
      ),
    );
  }

  public getAllNews(params: any): Observable<any> {
    const requestBody = this.newsRequestUtil.createNewsRequest(
      params.customParams,
      params.body,
    );
    return this.dataApiService
      .post('news', requestBody, {}, 'v4')
      .pipe(switchMap((respArg) => this.processNewsResponse(respArg)));
  }

  /**
   * Get a single news article (full content, tags, metadata).
   * Uses v2 news_item endpoint. GET for entity/portfolio context, POST for query context.
   * Enriches response with entity info for matches, events, and tags.
   */
  public getNewsItem(newsId: string, params: any): Observable<any> {
    const resolvedParams = this.buildNewsItemParams(params);
    const request$ = this.buildNewsItemRequest(newsId, params, resolvedParams);

    return request$.pipe(mergeMap((resp) => this.enrichNewsItem(resp)));
  }

  private buildNewsItemParams(params: any): Record<string, any> {
    const paramsToBeSent = [
      'event_ids',
      'factuality',
      'temporality',
      'participant',
      'tag',
      'from',
      'to',
      'lang',
    ];
    const isReader = this.routing.isReader() || this.routing.isStoryReader();
    const result = ParamsBuilder.build(
      params,
      paramsToBeSent,
      this.commonDataParams.project,
      { isReader },
    );

    this.widenForScoreEvent(params, result);

    // Event routing: normalize type/id for event contexts
    if (params.type === 'event') {
      if (['super_event', 'event', 'sub_event'].includes(params.id)) {
        if (
          params.eventype &&
          !['super_event', 'event', 'sub_event'].includes(params.eventype)
        ) {
          if (params.treemap?.split(',')[3] === params.eventid) {
            result['event_ids'] = params.treemap.split(',')[2];
          }
        } else if (!params.eventype) {
          result['has_events'] = 'true';
        }
      } else if (params.id) {
        result['event_ids'] = params.id;
      }
    }
    if (params.type === 'super_event' || params.type === 'sub_event') {
      result['event_ids'] = params.id;
    }
    if (
      params.eventid &&
      ['super_event', 'event', 'sub_event'].includes(params.eventype)
    ) {
      result['event_ids'] = params.eventid;
    }

    return result;
  }

  private buildNewsItemRequest(
    newsId: string,
    params: any,
    queryParams: Record<string, any>,
  ): Observable<any> {
    if (this.routing.isQuery() || this.routing.isSearch()) {
      return this.dataApiService.post(
        `search_query/news_item/${newsId}`,
        this.queryApi.query?.query,
        queryParams,
        'v2',
      );
    }

    // Determine entity type/id for URL
    // Only isMarket() and portfolioId contexts use specific type/id,
    // everything else (reader, folio, newsletter, etc.) uses all/all
    let type = 'all';
    let id = 'all';

    if (this.routing.isMarket() || params.portfolioId) {
      type = params.type || 'all';
      id = params.id || 'all';

      if (type === 'event') {
        if (['super_event', 'event', 'sub_event'].includes(id)) {
          if (
            params.eventype &&
            !['super_event', 'event', 'sub_event'].includes(params.eventype)
          ) {
            type = params.eventype;
            id = params.eventid;
          } else {
            type = 'all';
            id = 'all';
          }
        }
      } else if (id === 'icb' && type === 'hierarchy') {
        type = 'all';
        id = 'all';
      }
    } else if (this.routing.isNewsletter()) {
      if (params.type === 'portfolio' || params.type === 'search_query') {
        return this.dataApiService.post(
          `${params.type}/news_item/${newsId}`,
          params.payload,
          queryParams,
          'v2',
        );
      } else if (params.type && params.id) {
        type = params.type;
        id = params.id;
      }
    }

    return this.dataApiService.get(
      `${type}/${id}/news_item/${newsId}`,
      queryParams,
      'v2',
    );
  }

  private enrichNewsItem(respArg: any): Observable<any> {
    const resp = JSON.parse(JSON.stringify(respArg));
    const uniqueKeys = new Set<string>();

    resp.matches?.forEach((match: any) => {
      if (match.entity?.compound_key) uniqueKeys.add(match.entity.compound_key);
    });
    resp.events?.forEach((event: any) => {
      event.participants?.forEach((participant: any) => {
        if (participant.compound_key) uniqueKeys.add(participant.compound_key);
      });
    });
    resp.tags?.forEach((tag: any) => {
      const parsed = TagUtils.parse(tag.tag);
      if (parsed?.prefix === 'm') {
        uniqueKeys.add(parsed.key);
      }
    });

    if (!resp.title || uniqueKeys.size === 0) {
      return of(resp);
    }

    return this.entityInfoService.getEntitiesInfo([...uniqueKeys]).pipe(
      map((entityInfo: any) => {
        if (!resp.title || !resp.content) {
          return resp;
        }

        // Enrich event participants + adjust spans
        resp.events.forEach((event: any, indexEvent: number) => {
          if (event.participants) {
            event.participants.forEach(
              (participant: any, indexParticipant: number) => {
                resp.events[indexEvent].participants[indexParticipant] =
                  entityInfo[participant.compound_key];
              },
            );
          }
          if (event.span && resp.locations) {
            if (event.span.end <= resp.locations.title.end) {
              resp.events[indexEvent].where = 'title';
              resp.events[indexEvent].span.start -= resp.locations.title.start;
              resp.events[indexEvent].span.end -= resp.locations.title.start;
            } else {
              resp.events[indexEvent].where = 'content';
              resp.events[indexEvent].span.start -=
                resp.locations.content.start;
              resp.events[indexEvent].span.end -= resp.locations.content.start;
            }
          }
        });

        // Enrich matches + adjust spans
        resp.matches.forEach((match: any, indexMatch: number) => {
          match.entity = entityInfo[match.entity.compound_key];
          match.spans.forEach((span: any, indexSpan: number) => {
            if (span && resp.locations) {
              if (span.end <= resp.locations.title.end) {
                resp.matches[indexMatch].spans[indexSpan].location = 'title';
                resp.matches[indexMatch].spans[indexSpan].start -=
                  resp.locations.title.start;
                resp.matches[indexMatch].spans[indexSpan].end -=
                  resp.locations.title.start;
              } else {
                resp.matches[indexMatch].spans[indexSpan].location = 'content';
                resp.matches[indexMatch].spans[indexSpan].start -=
                  resp.locations.content.start;
                resp.matches[indexMatch].spans[indexSpan].end -=
                  resp.locations.content.start;
              }
            }
          });
        });
        resp.matches = resp.matches.filter(
          (match: any) =>
            !['source_name', 'source_uri'].includes(match.entity?.type),
        );

        // Enrich tags + adjust tag spans
        resp.tags.forEach((tag: any, indexTag: number) => {
          const parsed = TagUtils.parse(tag.tag);
          if (parsed?.prefix === 'm') {
            tag.word = entityInfo[parsed.key]?.name;
          }
          tag.spans?.forEach((span: any, indexSpan: number) => {
            if (span && resp.locations) {
              if (span.end <= resp.locations.title.end) {
                resp.tags[indexTag].spans[indexSpan].location = 'title';
                resp.tags[indexTag].spans[indexSpan].start -=
                  resp.locations.title.start;
                resp.tags[indexTag].spans[indexSpan].end -=
                  resp.locations.title.start;
              } else {
                resp.tags[indexTag].spans[indexSpan].location = 'content';
                resp.tags[indexTag].spans[indexSpan].start -=
                  resp.locations.content.start;
                resp.tags[indexTag].spans[indexSpan].end -=
                  resp.locations.content.start;
              }
            }
          });
        });

        // Adjust query highlights spans
        resp.query_highlights?.forEach(
          (highlight: any, indexHighlight: number) => {
            if (highlight.span && resp.locations) {
              if (highlight.span.end <= resp.locations.title.end) {
                resp.query_highlights[indexHighlight].where = 'title';
                resp.query_highlights[indexHighlight].span.start -=
                  resp.locations.title.start;
                resp.query_highlights[indexHighlight].span.end -=
                  resp.locations.title.start;
              } else {
                resp.query_highlights[indexHighlight].where = 'content';
                resp.query_highlights[indexHighlight].span.start -=
                  resp.locations.content.start;
                resp.query_highlights[indexHighlight].span.end -=
                  resp.locations.content.start;
              }
            }
          },
        );

        return resp;
      }),
      catchError(() => {
        if (resp.title && resp.content) {
          resp.events = [];
          resp.matches = [];
          resp.tags?.forEach((tag: any, indexTag: number) => {
            tag.spans?.forEach((span: any, indexSpan: number) => {
              if (span && resp.locations) {
                if (span.end <= resp.locations.title.end) {
                  resp.tags[indexTag].spans[indexSpan].location = 'title';
                  resp.tags[indexTag].spans[indexSpan].start -=
                    resp.locations.title.start;
                  resp.tags[indexTag].spans[indexSpan].end -=
                    resp.locations.title.start;
                } else {
                  resp.tags[indexTag].spans[indexSpan].location = 'content';
                  resp.tags[indexTag].spans[indexSpan].start -=
                    resp.locations.content.start;
                  resp.tags[indexTag].spans[indexSpan].end -=
                    resp.locations.content.start;
                }
              }
            });
          });
          resp.query_highlights = [];
          return of(resp);
        }
        return of(resp);
      }),
    );
  }

  // A single day pinned by the user wins; otherwise widen so a score driver's
  // older articles are reachable from the mosaic instead of returning nothing.
  private widenForScoreEvent(params: any, result: Record<string, any>): void {
    if (params.newsday || !params.scoreevent || !result['to']) {
      return;
    }
    const lookbackFrom = moment(result['to'])
      .subtract(SCORE_EVENT_LOOKBACK_DAYS, 'days')
      .format('YYYY-MM-DD');
    if (moment(result['from']).format('YYYY-MM-DD') > lookbackFrom) {
      result['from'] = lookbackFrom;
    }
  }

  /**
   * Get event snippets for the Events newsfeed tab.
   * Calls GET /api/{type}/{id}/event_snippets and maps to the standard { news: [...] } format.
   */
  public getEventSnippets(options: any): Observable<any> {
    const params = options.params;
    const entityType = params.type || 'all';
    const entityId = params.id || 'all';

    // Build full params using ParamsBuilder (same as old newsfeed method)
    const paramsToBeSent = [
      'size',
      'tag',
      'participant',
      ...ParamsBuilder.PARAMS_FOR_ALL_REQUESTS,
    ];
    const queryParams = ParamsBuilder.build(
      params,
      paramsToBeSent,
      this.commonDataParams.project,
    );
    this.widenForScoreEvent(params, queryParams);

    if (queryParams['tag']) {
      queryParams['co_occurrence'] =
        queryParams['tag'].split(':')[1] +
        ':' +
        queryParams['tag'].split(':')[2];
    }
    if (params.event_ids) {
      queryParams['event_ids'] = params.event_ids;
    }
    if (params.eventid) {
      queryParams['event_ids'] = params.eventid;
    }
    if (options.custom?.from_index && options.custom.from_index !== 0) {
      queryParams['from_index'] = options.custom.from_index;
    }
    if (options.custom?.size) {
      queryParams['size'] = options.custom.size;
    }
    queryParams['full_news'] = true;
    queryParams['collapse_similar_title'] = 'true';
    queryParams['deduplicate'] = 'true';
    if (params.duplicate === 'true') {
      queryParams['deduplicate'] = 'true';
    } else if (params.duplicate === 'false') {
      queryParams['collapse_similar_title'] = 'false';
      queryParams['deduplicate'] = 'false';
    }
    if (params.newstype === 'Events') {
      queryParams['full_news'] = true;
    }

    // Resolve entity: scanner uses tag, otherwise use type/id from params
    let resolvedType = entityType;
    let resolvedId = entityId;
    const parsedRequestTag =
      params.tag && !params.type ? TagUtils.parse(params.tag) : null;
    if (parsedRequestTag) {
      resolvedType = parsedRequestTag.type;
      resolvedId = parsedRequestTag.id;
    }

    return this.dataApiService
      .get<any>(`${resolvedType}/${resolvedId}/event_snippets`, queryParams)
      .pipe(
        map((resp) => {
          if (!resp?.event_snippets) {
            return resp;
          }
          const parsedResponseTag = TagUtils.parse(params.tag);
          return {
            news: resp.event_snippets.map((item: any) => {
              let type: string, alpha_id: string, compound_key: string;
              if (parsedResponseTag) {
                compound_key = parsedResponseTag.key;
                type = parsedResponseTag.type;
                alpha_id = parsedResponseTag.id;
              } else if (params.type && params.id) {
                compound_key = params.type + ':' + params.id;
                type = params.type;
                alpha_id = params.id;
              }
              return {
                reduce_visibility: false,
                document_categorization: item.document_categorization,
                has_events: [true],
                title: item.snippet,
                id: item.article_id,
                provider: item.provider,
                publish_time: item.article_publish_time,
                sentiment_proportions: item.sentiment_proportions,
                locations: {
                  title: {
                    start: item.event_span.start,
                    end: item.event_span.end,
                  },
                  content: {
                    start: item.event_span.start,
                    end: item.event_span.start,
                  },
                },
                events: item.events
                  .filter((el: any) => el.id === params.eventid)
                  .map((el: any) => ({
                    ...el,
                    span: {
                      start: item.event_span.start,
                      end: item.event_span.end,
                    },
                    where: 'title',
                  })),
                matches: [
                  {
                    implied: false,
                    entity: { alpha_id, type, compound_key },
                    spans: item.event_participant_spans.map((el: any) => ({
                      location: 'title',
                      implied: false,
                      is_coref: false,
                      start: el.start,
                      end: el.end,
                    })),
                  },
                ],
              };
            }),
          };
        }),
      );
  }
}
